import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { EditslotModel } from './editslot.model';
import { Component, Input, OnInit, OnChanges, EventEmitter, Output } from '@angular/core';
import * as moment from 'moment';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { ExerciseversionModel } from 'projects/core/src/lib/models/exerciseversion.model';
import { ExercisepartModel } from 'projects/core/src/lib/models/exercisepart.model';
import { SlotModel } from 'projects/core/src/lib/models/slot.model';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import {
  faStreetView,
  faUsers,
  faStar
} from '@fortawesome/pro-light-svg-icons';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { TranslocoService } from '@ngneat/transloco';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { ExerciseModel } from 'projects/runner/src/app/models/exercise.model';


@Component({
  selector: 'app-ac-timing',
  templateUrl: './ac-timing.component.html',
  styleUrls: ['./ac-timing.component.scss']
})
export class AcTimingComponent implements OnInit, OnChanges {

  @Input() ac: AcModel = null;
  @Input() mode = 'ac';
  @Output() setEdit = new EventEmitter<string>();

  public inEdit = false;
  public submitting = false;

  public slots: any = null;
  public editSlots: Array<EditslotModel> = [];
  public editSlot: EditslotModel = null;
  public isNew = false;
  public items: Array<MenuItem> = [];
  public errorMessages = {
    DURATION_TOO_LONG: this.transloco.translate('ac_timing.errors.duration_too_long'),
    STARTTIME_TOO_EARLY: this.transloco.translate('ac_timing.errors.block_starts_too_early'),
  };

  public timePicker: any = moment().set({
    hour: 9,
    minute: 0,
    second: 0
  }).toDate();

  public form: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl('', [Validators.required]),
    duration: new UntypedFormControl('', [Validators.required, Validators.min(0), Validators.max(180)]),
    relevance: this.fb.group({
      for_candidate: new UntypedFormControl(true),
      for_observer: new UntypedFormControl(true),
    }, {
      validator: this.relevanceValidator
    }),
  });
  public inheritVisible = false;

  faStreetView = faStreetView;
  faUsers = faUsers;
  faStar = faStar;

  constructor(
    private fb: UntypedFormBuilder,
    private api: ApiService,
    private ui: UiService,
    private auth: AuthStateService,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService
  ) {
    this.resetSlots();
  }

  ngOnInit(): void {
    this.buildAddMenu();
    this.form.get('relevance.for_candidate').valueChanges.subscribe((newValue: any) => {
      if (newValue === false && this.form.get('relevance.for_observer').value === false) {
        this.form.get('relevance.for_candidate').setValue(true);
      }
    });
    this.form.get('relevance.for_observer').valueChanges.subscribe((newValue: any) => {
      if (newValue === false && this.form.get('relevance.for_candidate').value === false) {
        this.form.get('relevance.for_observer').setValue(true);
      }
    });
    // a11y focus
    this.ui.setFocus();
  }

  timePickerChanged(): void {
    if (!this.ac.start_time && this.timePicker !== null) {
      if (this.inEdit === true) {
        this.editSlots.forEach((slot: EditslotModel) => {
          slot.setPseudoStarttime(this.timePicker);
        });
      } else {
        this.buildSlots();
      }
    }
  }

  ngOnChanges(): void {
    this.buildSlots();
  }

  onEdit(): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.startEdit();
    }
  }

  onCancel(): void {
    this.stopEdit();
  }

  startEdit(): void {
    this.inEdit = true;
    this.setEdit.emit('timing');
    this.addExerciseSlots();
    this.buildAddMenu();
    this.timePickerChanged();
    this.setReorderHandles();
  }

  stopEdit(): void {
    this.clearForm();
    this.editSlots = [];
    this.inEdit = false;
    this.setEdit.emit(null);
    // a11y focus
    this.ui.setFocus();
  }

  // edit

  onSlotAdd(type: string): void {
    const data: any = {
      id: moment().milliseconds(),
      type
    };
    switch (type) {
      case ('conference'): {
        data.name = this.transloco.translate('common.conference');
        data.for_candidate = false;
        data.for_observer = true;
        break;
      }
      case ('break'): {
        data.name = this.transloco.translate('common.break');
        data.for_candidate = true;
        data.for_observer = true;
        break;
      }
      default: {
        data.for_candidate = true;
        data.for_observer = true;
      }
    }
    const newSlot = new SlotModel().deserialize(data);
    const newEditSlot = new EditslotModel().deserialize({
      ac: this.ac,
      slot: newSlot
    });
    newEditSlot.setPseudoStarttime(this.timePicker);
    this.isNew = true;
    this.editSlots.push(newEditSlot);
    this.onSlotEdit(newEditSlot);
  }

  onRowReorder(event: any): void {
    // check if position is possible
    if (this.isReorderPossible(event.dragIndex, event.dropIndex)) {
      // recalculate timing
      this.recalculateTiming();
    } else {
      this.ui.toastError(this.transloco.translate('ac_timing.ordering_not_possible'), this.transloco.translate('common.error'), 5000);
    }
  }

  onSlotEdit(slot: EditslotModel): void {
    this.clearForm();
    this.editSlot = slot;
    this.form.patchValue({
      name: this.editSlot.name,
      relevance: {
        for_candidate: this.editSlot.for_candidate || false,
        for_observer: this.editSlot.for_observer || false,
      },
      duration: this.editSlot.duration
    });
    switch (this.editSlot.type) {
      case ('break'): {
        this.form.get('name').disable();
        if (this.ac?.isGroupAc()) {
          this.form.get('relevance.for_candidate').disable();
          this.form.get('relevance.for_observer').disable();
        } else {
          this.form.get('relevance.for_candidate').enable();
          this.form.get('relevance.for_observer').enable();
        }
        this.form.get('duration').enable();
        break;
      }
      case ('conference'):
      case ('onlinetest'):
      case ('exercisepart'): {
        this.form.get('name').disable();
        this.form.get('relevance.for_candidate').disable();
        this.form.get('relevance.for_observer').disable();
        this.form.get('duration').enable();
        break;
      }
      default: {
        this.form.get('name').enable();
        this.form.get('relevance.for_candidate').enable();
        this.form.get('relevance.for_observer').enable();
        this.form.get('duration').enable();
        break;
      }
    }
    // a11y focus
    this.ui.setFormFocus();
  }

  onSlotDelete(slot: EditslotModel): void {
    this.confirmation.confirm({
      message: this.transloco.translate('common.messages.really_delete', { value: slot.name }),
      header: this.transloco.translate('common.really_delete'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deleteSlot(slot);
        this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: slot.name }), this.transloco.translate('common.deleted'));
      }
    });
  }

  onCancelSlot(): void {
    this.clearForm();
    this.ui.hideMessage();
    if (this.isNew === true) {
      this.deleteSlot(this.editSlot);
    }
    this.editSlot = null;
    this.isNew = false;
  }

  onSubmitSlot(): boolean {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      const data = this.form.getRawValue();
      if (this.isNew === true) {
        data.start_time = this.getNewStarttime(true);
      }
      this.editSlot.setData(data);
      this.buildAddMenu();
      this.recalculateTiming();
      this.editSlot = null;
      this.isNew = false;
      return true;
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
      return false;
    }
  }

  private getAcStart(): any {
    return (this.ac.start_time_admin) ? this.ac.start_time_admin : this.timePicker;
  }

  onSubmit(): void {
    if (!this.editSlot || this.onSubmitSlot()) {
      if (this.ac.hasPlannedAcs()) {
        this.inheritVisible = true;
      } else {
        this.saveSlots(false);
      }
    }
  }

  private saveSlots(inherit: boolean): void {
    this.submitting = true;
    this.ui.hideMessage();
    const payload = {
      slots: this.editSlots.map((slot: EditslotModel) => {
        return {
          exercisepart_id: slot.exercisepart?.id || null,
          onlinetest_id: slot.onlinetest?.id || null,
          type: slot.type,
          name: slot.name,
          start_time: slot.start_time,
          duration: slot.duration,
          for_candidate: (slot.for_candidate === true),
          for_observer: (slot.for_observer === true),
        };
      }),
      inherit
    }
    this.api.saveSlots(this.mode + 's', this.ac.id, payload).subscribe({
      next: (result: any) => {
        if (result.data) {
          this.ac.deserialize(result.data);
          this.buildSlots();
          this.ui.toastSuccess(this.transloco.translate('ac_timing.timing_saved'), this.transloco.translate('common.added'));
          this.stopEdit();
        }
      },
      error: (error: any) => {
        this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
      }
    }).add(() => {
      this.submitting = false;
    });
  }

  private isReorderPossible(dragIndex: number, dropIndex: number): boolean {
    let result = true;
    if (this.ac.isSingleAc()) {
      // single: check if drag to this position is possible
      this.editSlots.forEach((slot: EditslotModel) => {
        if (slot.exercisepart) {
          const index = this.editSlots.indexOf(slot);
          for (let count = index + 1; count < this.editSlots.length; count++) {
            const compare: EditslotModel = this.editSlots[count];
            if (
              compare.exercisepart &&
              slot.exercisepart.exerciseversion?.id === compare.exercisepart.exerciseversion?.id &&
              slot.exercisepart.position > compare.exercisepart.position
            ) {
              result = false;
            }
          }
        }
      });
    } else {
      // group: check if drag to this position is possible
      if (dropIndex > 0 && dropIndex < this.editSlots.length) {
        const slotA = this.editSlots[dropIndex - 1];
        const slotB = this.editSlots[dropIndex + 1];
        if (slotA?.exercisepart?.exerciseversion?.exercise?.id !== undefined && slotA?.exercisepart?.exerciseversion?.exercise?.id === slotB?.exercisepart?.exerciseversion?.exercise?.id) {
          result = false;
        }
      }
      if (result === true) {
        // group: get also related slots to new position (exercises only)
        const sourceSlot = this.editSlots[dropIndex];
        if (sourceSlot.exercisepart?.exerciseversion) {
          const relatedSlots: Array<EditslotModel> = [];
          let relatedIndex = -1;
          do {
            relatedIndex = this.editSlots.findIndex(slot => slot.slot.id !== sourceSlot.slot.id && slot.exercisepart?.exerciseversion?.id === sourceSlot.exercisepart.exerciseversion.id);
            if (relatedIndex !== -1) {
              relatedSlots.push(this.editSlots.splice(relatedIndex, 1)[0]);
            }
          } while(relatedIndex !== -1);
          const newDropIndex = this.editSlots.findIndex(slot => slot.slot.id === sourceSlot.slot.id) || null;
          if (newDropIndex !== null) {
            relatedSlots.forEach((newSlot: EditslotModel, index: number) => {
              this.editSlots.splice(newDropIndex + 1 + index, 0, newSlot);
            });
          }
        }
      }
    }
    if (result !== true) {
      const backSlot: EditslotModel = this.editSlots.splice(dropIndex, 1)[0];
      this.editSlots.splice(dragIndex, 0, backSlot);
    }
    return result;
  }

  private recalculateTiming(): void {
    this.ui.showModalSpinner(this.transloco.translate('ac_timing.calculating_schedule'));
    const payload = this.editSlots.map((editSlot: EditslotModel) => {
      editSlot.errors = [];
      const slot: any = {...editSlot.slot};
      slot.exercisepart_id = slot.exercisepart?.id || null;
      slot.onlinetest_id = slot.onlinetest?.id || null;
      delete slot.exercisepart;
      return slot;
    });
    this.api.getRecalculatedTiming(this.ac.id, payload).subscribe({
      next: (result: any) => {
        if (result.data?.length > 0) {
          result.data.forEach((entry: any) => {
            const editSlot = this.editSlots.find((actual: EditslotModel) => actual.slot.id === entry.id);
            if (editSlot !== undefined) {
              editSlot.slot.start_time = entry.start_time;
              editSlot.slot.duration = entry.duration;
              editSlot.errors = entry.errors;
            }
          });
        }
      }
    }).add(() => {
      this.ui.hideModalSpinner();
    });
  }

  private deleteSlot(slot: EditslotModel): void {
      const index = this.editSlots.indexOf(slot);
      if (index > -1) {
        this.editSlots.splice(index, 1);
      }
  }

  private addExerciseSlots(): void {
    this.editSlots = this.ac.slots.map((slot: SlotModel) => {
      return new EditslotModel().deserialize({
          ac: this.ac,
          slot,
          exercisepart: slot.exercisepart,
      });
    });
    this.ac.exerciseversions.forEach((exerciseversion: ExerciseversionModel, index1: number) => {
      exerciseversion.exerciseparts.sort((a: any, b: any) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0));
      exerciseversion.exerciseparts.forEach((exercisepart: ExercisepartModel, index2: number) => {
        if (exercisepart.type === 'normal' && this.editSlots.find(slot => slot.exercisepart?.id === exercisepart.id) === undefined) {
          this.editSlots.push(new EditslotModel().deserialize({
            ac: this.ac,
            slot: new SlotModel().deserialize({
              id: (index1 * 10000) + (index2 * 1000) + moment().milliseconds(),
              start_time: this.getNewStarttime(),
              type: 'exercisepart',
              duration: exercisepart.duration,
              exercisepart
            }),
            exercisepart,
          }));
        }
      });
    });
    // @todo Onlinetests?
  }

  private getNewStarttime(isNew = false): number {
    let timer = 0;
    if (this.editSlots.length > 0) {
      const offset = (isNew === true) ? 2 : 1;
      const last = this.editSlots[this.editSlots.length - offset];
      if (last) {
        timer = last.getEndTimeAsMinutes();
      }
    }
    return timer;
  }

  private clearForm(): void {
    this.form.reset();
    this.form.patchValue({
      name: null,
      duration: null,
      relevance: {
        for_candidate: true,
        for_observer: true,
      },
    });
  }

  private relevanceValidator(control: UntypedFormGroup): any {
    let result = { custom: true };
    if (control) {
      Object.keys(control.value).forEach((key: any) => {
        if (control.value[key] === true) {
          result = null;
        }
      });
    }
    return result;
  }

  private buildAddMenu(): void {
    this.items = [];
    this.items.push({
      label: this.transloco.translate('common.free_block'),
      icon: 'pi pi-users',
      command: () => {
        this.onSlotAdd('org');
      }
    });
    this.items.push({
      label: this.transloco.translate('common.break'),
      icon: 'pi pi-moon',
      command: () => {
        this.onSlotAdd('break');
      }
    });
    if (this.editSlots.find(slot => slot.type === 'conference') === undefined) {
      this.items.push({
        label: this.transloco.translate('common.conference'),
        icon: 'pi pi-chart-bar',
        command: () => {
          this.onSlotAdd('conference');
        }
      });
    }
  }

  // view

  hasSlots(): boolean {
    return this.slots !== null && (this.slots?.candidates?.length > 0 || this.slots?.observer?.length > 0);
  }

  private resetSlots(): void {
    this.slots = {
      candidate: [],
      observer: [],
    };
  }

  private buildSlots(): void {
    const start_time = this.getAcStart();
    this.resetSlots();
    this.ac.slots.sort((a: any, b: any) => (a.start_time > b.start_time) ? 1 : ((b.start_time > a.start_time) ? -1 : 0));

    ['candidate', 'observer'].forEach((agenda: string) => {
      this.ac.slots.forEach((slot: SlotModel) => {
        let value: any = null;
        let lastDuration = 0;
        if (
          (agenda === 'candidate' && slot.is_for_candidate === true) ||
          (agenda === 'observer' && slot.is_for_observer === true)
        ) {
          switch (slot.type) {
            case('exercisepart'): {
              if (slot.exercisepart !== null) {
                value = {
                  time: moment(start_time).add(slot.start_time, 'minutes'),
                  icon: slot.icon,
                  name: slot.exercisepart.exerciseversion.exercise.title + ': ' + slot.exercisepart.name,
                  duration: slot.exercisepart.duration,
                  type: slot.type
                };
              }
              lastDuration = slot.exercisepart.duration;
              break;
            }
            case('onlinetest'): {
              if (slot.onlinetest !== null) {
                value = {
                  time: moment(start_time).add(slot.start_time, 'minutes'),
                  icon: slot.icon,
                  name: this.transloco.translate('common.labels.onlinetest') + ': ' + slot.onlinetest.name,
                  duration: slot.duration,
                  type: slot.type
                };
              }
              lastDuration = slot.duration;
              break;
            }
            default: {
              value = {
                time: moment(start_time).add(slot.start_time, 'minutes'),
                icon: slot.icon,
                name: slot.name,
                duration: slot.duration,
                type: slot.type
              };
              break;
            }
          }
          if (value !== null) {
            this.slots[agenda].push(value);
          }
          // bewertung (nur beobachter)
          if (agenda === 'observer' && slot.type === 'exercisepart' && slot.exercisepart.rating_start !== 'none') {
            let ratingDuration = null;
            let ratingStart = slot.start_time;
            if (slot.exercisepart.rating_start === 'end') {
              ratingDuration = slot.exercisepart.rating_duration;
              ratingStart = slot.start_time + slot.duration;
            } else if (slot.exercisepart.rating_start === 'start' && slot.exercisepart.rating_duration > lastDuration) {
              ratingDuration = slot.exercisepart.rating_duration - lastDuration;
            }
            if (ratingDuration !== null) {
              this.slots[agenda].push({
                name: slot.exercisepart.exerciseversion.exercise.title + ': ' + this.transloco.translate('common.labels.rating'),
                time: moment(start_time).add(ratingStart, 'minutes'),
                icon: 'pi pi-star',
                duration: ratingDuration,
                type: 'rating'
              });
            }
          }
        }
      });
    });
  }

  private setReorderHandles(): void {
    if (this.ac.isGroupAc()) {
      const exerciseIds: Array<number> = [];
      this.editSlots.forEach((slot: EditslotModel) => {
        const exerciseId = slot.exercisepart?.exerciseversion?.exercise?.id || null;
        if (exerciseId !== null) {
          if (!exerciseIds.includes(exerciseId)) {
            exerciseIds.push(exerciseId);
          } else {
            slot.showReorderHandle = false;
          }
        }
      });
    }
  }

}
